import React, { useState, useRef, useEffect } from 'react';
import { TextInput } from '@orderly/morrisons-component-library';
import './TimePicker.scss';

interface ITimePickerProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  error?: string;
  required?: boolean;
}

const TimePicker: React.FC<ITimePickerProps> = ({
  label,
  name,
  value,
  onChange,
  onClear,
  error = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // Parse value into hour and minute when it changes
  useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      setSelectedHour(h);
      setSelectedMinute(m);
    } else {
      setSelectedHour('');
      setSelectedMinute('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = ['00', '30'];

  const handleHourSelect = (hour: string) => {
    setSelectedHour(hour);
    if (selectedMinute) {
      onChange(`${hour}:${selectedMinute}`);
    }
  };

  const handleMinuteSelect = (minute: string) => {
    setSelectedMinute(minute);
    if (selectedHour) {
      onChange(`${selectedHour}:${minute}`);
      setIsOpen(false);
    }
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const hasValue = value && value.trim() !== '';

  return (
    <div className="time-picker-container" ref={containerRef}>
      <div
        ref={inputRef}
        onClick={handleInputClick}
        className={`time-input-click-wrapper ${hasValue ? 'has-value' : ''}`}
      >
        <TextInput
          label={label}
          name={name}
          type="text"
          value={value}
          onChange={() => {}} // Controlled input, actual change handled by dropdown
          error={error}
        />
      </div>
      {hasValue && (
        <button
          type="button"
          className="time-clear-button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
            setSelectedHour('');
            setSelectedMinute('');
          }}
          aria-label="Clear time"
          title="Clear time"
        >
          ×
        </button>
      )}

      {isOpen && (
        <div className="time-dropdown">
          <div className="time-columns">
            <div className="hour-column">
              <div className="column-header">Hour</div>
              <div className="time-options">
                {hours.map(hour => (
                  <div
                    key={hour}
                    className={`time-option ${selectedHour === hour ? 'selected' : ''}`}
                    onClick={() => handleHourSelect(hour)}
                  >
                    {hour}
                  </div>
                ))}
              </div>
            </div>
            <div className="minute-column">
              <div className="column-header">Minute</div>
              <div className="time-options">
                {minutes.map(minute => (
                  <div
                    key={minute}
                    className={`time-option ${selectedMinute === minute ? 'selected' : ''}`}
                    onClick={() => handleMinuteSelect(minute)}
                  >
                    {minute}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
